import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai/react'
import { X } from 'lucide-react'

import { LoginStatus } from '@/constants/common'
import { Theme } from '@/constants/theme'
import useTheme from '@/hooks/useTheme'
import { useWallet } from '@/hooks/useWallet'
import useSolana from '@/lib/solana/hooks/useSolana'
import { cn } from '@/lib/utils'
import { services } from '@/stores'

import PostForm from '../coin/PostForm'
import Trade from '../coin/Trade'
import ProfileForm from '../layout/ProfileForm'
import { Profile } from '../svgr/common'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../ui/dialog'

import { Flex } from './Box'
import { Button, SecondaryButton } from './Button'
import { Text } from './Text'

export const ProDialog = React.forwardRef<
  {
    open: () => void
    close: () => void
  },
  React.ComponentProps<typeof Dialog> & {
    hiddenClose?: boolean
    title?: React.ReactNode
    triggerProps?: React.ComponentProps<typeof DialogTrigger>
    descriptionProps?: React.ComponentProps<typeof DialogDescription>
    contentProps?: Omit<
      React.ComponentProps<typeof DialogContent>,
      'children'
    > & { disableOutsideClose?: boolean }
    footerProps?: React.ComponentProps<typeof DialogFooter>
    onOpen?: () => void
    onClose?: () => void
  }
>(
  (
    {
      children,
      hiddenClose = false,
      title,
      triggerProps,
      descriptionProps,
      contentProps,
      footerProps,
      open: _open,
      onOpen,
      onClose,
      onOpenChange: _onOpenChange,
      ...props
    },
    ref
  ) => {
    const { connecting } = useSolana()
    const [open, setOpen] = useState(false)
    const { disableOutsideClose, ...contentPropsRest } = contentProps || {}
    const fnInteractOutside = React.useCallback((e: any) => {
      const target = e.target as HTMLElement
      // 点击 DialogClose 按钮，允许关闭
      if (target.closest('[data-dialog-close]')) {
        return
      }
      // 点遮罩，阻止默认行为，不关闭
      e.preventDefault()
    }, [])

    const onOpenChange = useCallback(
      (open: boolean) => {
        setOpen(open)
        if (open) {
          onOpen?.()
        } else {
          onClose?.()
        }
      },
      [onClose, onOpen]
    )

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setOpen(true)
        },
        close: () => {
          setOpen(false)
        }
      }),
      []
    )

    return (
      <Dialog
        // 当钱包连接工具打开是，这边弹窗关闭
        open={(_open ?? open) && !connecting}
        onOpenChange={_onOpenChange ?? onOpenChange}
        {...props}
      >
        {triggerProps && <DialogTrigger asChild {...triggerProps} />}
        <DialogContent
          onInteractOutside={
            disableOutsideClose ? fnInteractOutside : undefined
          }
          {...contentPropsRest}
          className={cn(
            'w-[calc(100vw-24px)] !rounded xl:mx-0 xl:max-w-max [&>button]:hidden',
            'p-4 xl:p-8',
            contentProps?.className
          )}
        >
          <DialogHeader>
            <Flex className="flex-row items-center justify-between gap-2">
              <DialogTitle className="font-Orbitron flex-1 text-left text-sm xl:text-xl">
                {title}
              </DialogTitle>
              {!hiddenClose && (
                <DialogClose
                  role="dialog-close"
                  className="!mt-0 flex items-center justify-center"
                >
                  <X className="size-5 xl:size-6" />
                </DialogClose>
              )}
            </Flex>
            {descriptionProps && (
              <DialogDescription
                {...descriptionProps}
                className={cn('text-left', descriptionProps.className)}
              />
            )}
          </DialogHeader>
          {children}
          {footerProps && <DialogFooter {...footerProps} />}
        </DialogContent>
      </Dialog>
    )
  }
)

export const TradeDialog: React.FC<{ mint: string }> = ({ mint }) => {
  const { t } = useTranslation()
  const { isConnected } = useSolana()
  const { connect: connectWallet } = useWallet()
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          border={false}
          className="h-10 flex-1 font-Orbitron"
          onClick={
            !isConnected
              ? (ev) => {
                  ev.stopPropagation()
                  ev.preventDefault()
                  connectWallet()
                }
              : undefined
          }
        >
          {!isConnected ? t('common.connectWallet') : t('coin.trade.label')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[calc(100vw-24px)] p-3">
        <DialogHeader>
          <DialogTitle className="text-left font-Orbitron text-sm">
            {t('coin.trade.label')}
          </DialogTitle>
        </DialogHeader>
        <Trade mint={mint} />
      </DialogContent>
    </Dialog>
  )
}

export const PostDialog = React.forwardRef<
  {
    onOpen: (replyId: number) => void
    onClose: () => void
  },
  { refresh: () => void; replyCount: number }
>(({ refresh, replyCount }, ref) => {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const [open, setOpen] = useState(false)
  const [replyId, setReplyId] = useState<number>()
  const loginStatus = useAtomValue(services.loginStatusAtom)
  const { connect } = useWallet()

  useImperativeHandle(
    ref,
    () => ({
      onOpen: (replyId: number) => {
        setReplyId(replyId)
        setOpen(true)
      },
      onClose: () => {
        setOpen(false)
      }
    }),
    []
  )

  useEffect(() => {
    if (!open) setReplyId(undefined)
  }, [open])

  return loginStatus !== LoginStatus.LOGIN ? (
    <SecondaryButton
      size="xs"
      onClick={connect}
      border={false}
      variant={'primary'}
    >
      {t('common.connectWallet')}
    </SecondaryButton>
  ) : (
    <ProDialog
      open={open}
      onOpenChange={setOpen}
      title={
        <>
          {t('coin.addComment')}
          {replyId ? ` [${t('coin.replyTo', { replyId })}` : ''}
        </>
      }
      triggerProps={{
        asChild: true,
        children: (
          <SecondaryButton
            role="post-reply"
            size="xs"
            border={false}
            variant={'primary'}
          >
            <span>Add Comment</span>
            {replyCount
              ? ` [${t('coin.replyCount', { replyCount: replyCount + '' || '--' })}]`
              : ''}
          </SecondaryButton>
        )
      }}
      contentProps={{ className: 'xl:max-w-[640px]' }}
    >
      <PostForm
        replyId={replyId}
        close={() => {
          setOpen(false)
          refresh()
        }}
      />
    </ProDialog>
  )
})
PostDialog.displayName = 'PostDialog'

export const ProfileDialog = () => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <ProDialog
      open={open}
      onOpenChange={setOpen}
      title={t('wallet.viewProfile')}
      triggerProps={{
        asChild: true,
        children: (
          <Button role="view-profile" size="sm" ghost>
            <Profile className="text-color-text-primary" />
            <Text className="font-light">{t('wallet.viewProfile')}</Text>
          </Button>
        )
      }}
      contentProps={{ className: 'xl:max-w-[540px]' }}
    >
      <ProfileForm onClose={() => setOpen(false)} />
    </ProDialog>
  )
}
